// The routing window (spec §5): Window opens = FIRST SHIP DATE − CUST_REF1 business days,
// Route by = CANCEL DATE − CUST_REF2 business days. Business days skip weekends and the holidays
// passed in (`days.ts`), so both dates move back past a holiday.
//
// The numbers are `routingRuleFor`'s (`routing-rules.ts`): an app rule, else A2000's.
// Worked out per pick ticket, with its own customer's rule and its own dates; the line takes the
// earliest Window opens and the earliest Route by (Isaac: the strictest one wins, when one WMS
// holds several customers or dates). A pick ticket with no rule counts for neither; "No routing
// rule" only when none has one.

import type { StatusCategory } from "./routing-status";
import type { Holidays } from "./days";
import type { RuleSource } from "./routing-rules";
import { addBusinessDays, businessDaysBetween, formatShortDay, holidaysBetween } from "./days";

export type WindowState =
  "overdue" | "today" | "tomorrow" | "due" | "open" | "requested" | "notyet" | "norule" | "routed" | "hold";

/** The default order of the Worklist: most urgent first. */
export const WINDOW_RANK: Record<WindowState, number> = {
  overdue: 0,
  today: 1,
  tomorrow: 2,
  due: 3,
  open: 4,
  requested: 5,
  notyet: 6,
  norule: 7,
  hold: 8,
  routed: 9,
};

/** Each state as one word for it, without the day counts its label carries. */
export const WINDOW_STATE_LABELS: Record<WindowState, string> = {
  overdue: "Past deadline",
  today: "Route today",
  tomorrow: "Route by tomorrow",
  due: "Due in 2–3 days",
  open: "Window open",
  requested: "Requested · waiting",
  notyet: "Not open yet",
  norule: "No routing rule",
  hold: "On hold",
  routed: "Routed",
};

export interface RoutingWindow {
  state: WindowState;
  label: string;
  /** Null without a routing rule or dates. */
  opens: string | null;
  routeBy: string | null;
  /** Business days to Route by (negative: late); null when there is none. */
  left: number | null;
  /** The rule of the pick ticket that set Window opens, and of the one that set Route by. */
  openBdays: number | null;
  dueBdays: number | null;
  /** Which pick ticket (and customer) set each date: the strictest one. */
  opensBy: WindowSetter | null;
  routeBySetBy: WindowSetter | null;
  /** The holidays Window opens and Route by stepped over (none on a weekend). */
  skipped: { day: string; title: string }[];
}

export interface WindowSetter {
  pickTicket: string;
  customer: string;
  /** Where its rule came from (`routing-rules.ts`): the hover says it. */
  rule?: RuleSource;
}

/** One pick ticket's dates and its customer's rule. */
export interface WindowPt {
  pickTicket: string;
  customer: string;
  start: string | null;
  cancel: string | null;
  openBdays: number | null;
  dueBdays: number | null;
  /** Where the rule came from: an app rule, A2000's CUST_REF1/2… (`routingRuleFor`). */
  rule?: RuleSource;
}

export interface WindowInput {
  pts: readonly WindowPt[];
  /** Its Routing status's category (`routing-status.ts`), never the value's words. */
  routingCategory: StatusCategory;
}

/** The earliest date among the pick tickets, and the one that set it (a tie: the lower PT #). */
function strictest(
  pts: readonly WindowPt[],
  date: (p: WindowPt) => string | null,
): { day: string; by: WindowPt } | null {
  let best: { day: string; by: WindowPt } | null = null;
  for (const p of pts) {
    const d = date(p);
    if (d && (!best || d < best.day || (d === best.day && p.pickTicket.localeCompare(best.by.pickTicket) < 0)))
      best = { day: d, by: p };
  }
  return best;
}

export function routingWindow(line: WindowInput, today: string, holidays: Holidays): RoutingWindow {
  const open = strictest(line.pts, (p) =>
    p.openBdays !== null && p.start ? addBusinessDays(p.start, -p.openBdays, holidays) : null,
  );
  const due = strictest(line.pts, (p) =>
    p.dueBdays !== null && p.cancel ? addBusinessDays(p.cancel, -p.dueBdays, holidays) : null,
  );
  const opens = open?.day ?? null;
  const routeBy = due?.day ?? null;
  const left = routeBy ? businessDaysBetween(today, routeBy, holidays) : null;
  const skipped = [
    ...new Map(
      [
        ...(open && open.by.start ? holidaysBetween(open.day, open.by.start, holidays) : []),
        ...(due && due.by.cancel ? holidaysBetween(due.day, due.by.cancel, holidays) : []),
      ].map((h) => [h.day, h]),
    ).values(),
  ].sort((a, b) => a.day.localeCompare(b.day));
  const setter = (p: WindowPt | undefined) =>
    p ? { pickTicket: p.pickTicket, customer: p.customer, ...(p.rule ? { rule: p.rule } : {}) } : null;
  const base = {
    opens,
    routeBy,
    left,
    openBdays: open?.by.openBdays ?? null,
    dueBdays: due?.by.dueBdays ?? null,
    opensBy: setter(open?.by),
    routeBySetBy: setter(due?.by),
    skipped,
  };
  const c = line.routingCategory;
  // Spec §5: "Routed" for Routed and Pre-routed, "On hold" for Need info and Need extension — now
  // whatever value the list puts in that category.
  if (c === "Routed") return { ...base, state: "routed", label: "Routed" };
  if (c === "On hold" || c === "Cancelled") return { ...base, state: "hold", label: "On hold" };
  if (c === "Requested") return { ...base, state: "requested", label: "Requested · waiting" };
  if (!opens) return { ...base, state: "norule", label: "No routing rule" };
  // An opening rule without a deadline (CUST_REF2 empty): it opens, and stays open.
  if (!routeBy || left === null) {
    return today < opens
      ? { ...base, state: "notyet", label: `Opens in ${businessDaysBetween(today, opens, holidays)} days` }
      : { ...base, state: "open", label: "Window open · no deadline" };
  }
  // A cancel date before the first ship date (bad source data) can put Route by before the
  // window opens: then the deadline wins over "Opens …".
  if (today < opens && opens <= routeBy) {
    return {
      ...base,
      state: "notyet",
      label: `Opens ${formatShortDay(opens)} · in ${businessDaysBetween(today, opens, holidays)}d`,
    };
  }
  if (left < 0) return { ...base, state: "overdue", label: `Past deadline · ${-left}d late` };
  if (left === 0) return { ...base, state: "today", label: "Route today" };
  if (left === 1) return { ...base, state: "tomorrow", label: "Route by tomorrow" };
  if (left <= 3) return { ...base, state: "due", label: `Due in ${left} days` };
  return { ...base, state: "open", label: `Window open · ${left}d left` };
}

/**
 * The window as the grid's pinned cell says it, short: "Past 10/30", "Route now", "Open · 11/06",
 * "Open", "Requested", "Not yet", "No rule", "Turned off", "On hold", "Routed". The whole sentence
 * (`label`: "Window open · no deadline") is the cell's hover and the line details'; the Worklist's
 * tabs, counters and filters read `state`, never this. `turnedOff`: the app's No routing rule is
 * why there is none (`ruleTurnedOff`).
 */
export function windowShort(w: Pick<RoutingWindow, "state" | "routeBy">, turnedOff = false): string {
  switch (w.state) {
    case "overdue":
      return w.routeBy ? `Past ${formatShortDay(w.routeBy)}` : "Past deadline";
    case "today":
    case "tomorrow":
      return "Route now";
    case "due":
    case "open":
      return w.routeBy ? `Open · ${formatShortDay(w.routeBy)}` : "Open";
    case "requested":
      return "Requested";
    case "notyet":
      return "Not yet";
    case "norule":
      return turnedOff ? "Turned off" : "No rule";
    case "hold":
      return "On hold";
    case "routed":
      return "Routed";
  }
}
